import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api.ts";
import PatientSelector from "./PatientSelector.jsx";
import DatasetFolderUpload from "./DatasetFolderUpload.jsx";
import DatasetZipUpload from "./DatasetZipUpload.jsx";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Navbar from "../navbar/Navbar";
import "./Datasets.css";

const MedicalDatasetCreator = () => {
    const navigate = useNavigate();
    const [datasetPatients, setDatasetPatients] = useState([]);
    const [datasetName, setDatasetName] = useState("");
    const [datasetDescription, setDatasetDescription] = useState("");
    const [activeSection, setActiveSection] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const handlePatientsCreated = (createdPatients) => {
        // Add created patients to the dataset
        const newPatients = createdPatients.map(p => ({
            id: p.id,
            patient_id: p.patient_id,
            data_count: { total: p.total_items }
        }));
        setDatasetPatients(prev => [...prev, ...newPatients]);
        toast.success(`Added ${createdPatients.length} patients to dataset`);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!datasetName.trim()) {
            toast.warning("Please enter a dataset name.");
            return;
        }

        if (datasetPatients.length === 0) {
            toast.warning("Please add at least one patient to the dataset.");
            return;
        }

        setSubmitting(true);
        const formData = new FormData();

        formData.append("name", datasetName);
        formData.append("description", datasetDescription);
        formData.append("created_by", localStorage.getItem("username"));

        // Add patients
        datasetPatients.forEach((patient) => {
            formData.append("patients", patient.id);
        });

        try {
            await API.post("/datasets/", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Dataset created successfully.");
            navigate("/Datasets");
        } catch (err) {
            console.error("Error creating dataset:", err);
            toast.error("Failed to create dataset.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="datasets-page">
            <video autoPlay muted loop className="video-background">
                <source src="/assets/stars.mp4" type="video/mp4" />
            </video>
            <div className="video-overlay"></div>

            <Navbar />
            <ToastContainer autoClose={3000} />

            <div className="container py-4">
                {/* Back Button */}
                <button
                    className="back-button"
                    onClick={() => navigate("/Datasets")}
                >
                    <i className="fa fa-arrow-left"></i>
                    Back to Datasets
                </button>

                {/* Header Card */}
                <div className="datasets-header-card">
                    <div className="datasets-header-content">
                        <div className="datasets-info">
                            <h1 className="datasets-title">Create New Dataset</h1>
                            <p className="datasets-description">
                                Add patients to your dataset using the options below
                            </p>
                        </div>
                        <div className="dataset-meta-info">
                            <div className="meta-stat">
                                <span className="meta-value">{datasetPatients.length}</span>
                                <span className="meta-label">Patients Added</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Dataset Form Card */}
                <div className="datasets-card mb-4">
                    <div className="datasets-card-header">
                        <div className="datasets-card-title">
                            <i className="fa fa-info-circle datasets-card-icon"></i>
                            <div>
                                <h3>Dataset Information</h3>
                                <p className="datasets-card-subtitle">Enter the basic details for your dataset</p>
                            </div>
                        </div>
                    </div>
                    <div className="datasets-card-content" style={{ padding: "1.5rem" }}>
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="form-group mb-3">
                                        <label className="form-label">Dataset Name *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter dataset name"
                                            value={datasetName}
                                            onChange={(e) => setDatasetName(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group mb-3">
                                        <label className="form-label">Description</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter dataset description"
                                            value={datasetDescription}
                                            onChange={(e) => setDatasetDescription(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex justify-content-end">
                                <button
                                    type="submit"
                                    className="btn-create-dataset"
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa fa-check me-2"></i>
                                            Create Dataset
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Add Patients Section */}
                <div className="datasets-card mb-4">
                    <div className="datasets-card-header">
                        <div className="datasets-card-title">
                            <i className="fa fa-users datasets-card-icon"></i>
                            <div>
                                <h3>Add Patients</h3>
                                <p className="datasets-card-subtitle">Add existing patients, upload a folder, or upload a ZIP file</p>
                            </div>
                        </div>
                    </div>
                    <div className="datasets-card-content" style={{ padding: "1.5rem" }}>
                        <div className="add-images-buttons">
                            <button
                                type="button"
                                className={`add-image-btn ${activeSection === "patients" ? "active" : ""}`}
                                onClick={() => setActiveSection(activeSection === "patients" ? null : "patients")}
                                style={{ background: activeSection === "patients" ? 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)' : '' }}
                            >
                                <i className="fa fa-users"></i>
                                <span>Add Existing Patients</span>
                                {datasetPatients.length > 0 && (
                                    <span className="badge bg-success ms-2">{datasetPatients.length}</span>
                                )}
                            </button>
                            <button
                                type="button"
                                className={`add-image-btn ${activeSection === "upload" ? "active" : ""}`}
                                onClick={() => setActiveSection(activeSection === "upload" ? null : "upload")}
                                style={{ background: activeSection === "upload" ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' : '' }}
                            >
                                <i className="fa fa-folder-open"></i>
                                <span>Upload Folder</span>
                            </button>
                            <button
                                type="button"
                                className={`add-image-btn ${activeSection === "zip" ? "active" : ""}`}
                                onClick={() => setActiveSection(activeSection === "zip" ? null : "zip")}
                                style={{ background: activeSection === "zip" ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : '' }}
                            >
                                <i className="fa fa-file-archive"></i>
                                <span>Upload ZIP File</span>
                            </button>
                        </div>

                        {activeSection && (
                            <div className="add-images-form mt-4">
                                {activeSection === "patients" && (
                                    <PatientSelector
                                        selectedPatients={datasetPatients}
                                        onPatientsChange={setDatasetPatients}
                                    />
                                )}
                                {activeSection === "upload" && (
                                    <DatasetFolderUpload
                                        onPatientsCreated={handlePatientsCreated}
                                    />
                                )}
                                {activeSection === "zip" && (
                                    <DatasetZipUpload
                                        onPatientsCreated={handlePatientsCreated}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Added Patients Summary Card */}
                {datasetPatients.length > 0 && (
                    <div className="datasets-card">
                        <div className="datasets-card-header">
                            <div className="datasets-card-title">
                                <i className="fa fa-list datasets-card-icon"></i>
                                <div>
                                    <h3>Patients to Add</h3>
                                    <p className="datasets-card-subtitle">
                                        {datasetPatients.length} patient{datasetPatients.length !== 1 ? 's' : ''} will be added to this dataset
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => setDatasetPatients([])}
                            >
                                <i className="fa fa-trash me-1"></i>
                                Clear All
                            </button>
                        </div>
                        <div className="datasets-card-content" style={{ padding: "1rem" }}>
                            <div className="patients-summary-grid">
                                {datasetPatients.map((patient, index) => (
                                    <div key={patient.id || index} className="patient-summary-item">
                                        <div className="patient-summary-info">
                                            <i className="fa fa-user me-2"></i>
                                            <strong>{patient.patient_id}</strong>
                                            {patient.data_count?.total > 0 && (
                                                <span className="badge bg-secondary ms-2">
                                                    {patient.data_count.total} files
                                                </span>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-link text-danger btn-sm p-0"
                                            onClick={() => {
                                                setDatasetPatients(prev => prev.filter(p => p.id !== patient.id));
                                            }}
                                            title="Remove patient"
                                        >
                                            <i className="fa fa-times"></i>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                .patients-summary-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
                    gap: 0.75rem;
                }

                .patient-summary-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    background: rgba(0, 0, 0, 0.03);
                    padding: 0.75rem 1rem;
                    border-radius: 8px;
                    transition: background 0.2s ease;
                    border: 1px solid rgba(0, 0, 0, 0.08);
                }

                .patient-summary-item:hover {
                    background: rgba(0, 0, 0, 0.06);
                }

                .patient-summary-info {
                    display: flex;
                    align-items: center;
                    color: #333;
                }

                .patient-summary-info i {
                    color: #666;
                }
            `}</style>
        </div>
    );
};

export default MedicalDatasetCreator;
